import { useState, useEffect, useCallback, CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { RechargeRequest } from '../../types';
import { adminListRechargeRequests, adminApproveRechargeRequest, adminRejectRechargeRequest } from '../../api';
import { creditsToYuan } from '../../utils/currency';
import { useToast } from '../../components/Toast';
import Modal from '../../components/Modal';
import EmptyState from '../../components/EmptyState';
import Breadcrumb from '../../components/Breadcrumb';

type Tab = 'pending' | 'reviewed';

function formatTime(s: string): string {
  if (!s) return '-';
  return new Date(s).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export default function AdminRechargeRequests() {
  const { t } = useTranslation('admin');
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('pending');
  const [items, setItems] = useState<RechargeRequest[]>([]);
  const [loading, setLoading] = useState(false);

  // approve modal
  const [approveTarget, setApproveTarget] = useState<RechargeRequest | null>(null);
  const [approveAmount, setApproveAmount] = useState('');
  const [approveRemark, setApproveRemark] = useState('');
  // reject modal
  const [rejectTarget, setRejectTarget] = useState<RechargeRequest | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    adminListRechargeRequests(tab, 1, 100)
      .then(res => setItems(res.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const openApprove = (r: RechargeRequest) => {
    setApproveTarget(r);
    setApproveAmount(String(r.requestedAmount / 100));
    setApproveRemark('');
  };

  const submitApprove = async () => {
    if (!approveTarget) return;
    const yuan = parseFloat(approveAmount);
    if (!yuan || yuan <= 0) { toast.error(t('rechargeRequests.creditPositive')); return; }
    setBusy(true);
    try {
      await adminApproveRechargeRequest(approveTarget.id, Math.round(yuan * 100), approveRemark.trim());
      toast.success(t('rechargeRequests.approvedToast'));
      setApproveTarget(null);
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('rechargeRequests.approveFailed'));
    } finally {
      setBusy(false);
    }
  };

  const submitReject = async () => {
    if (!rejectTarget) return;
    if (!rejectReason.trim()) { toast.error(t('rechargeRequests.reasonRequired')); return; }
    setBusy(true);
    try {
      await adminRejectRechargeRequest(rejectTarget.id, rejectReason.trim());
      toast.success(t('rechargeRequests.rejectedToast'));
      setRejectTarget(null);
      setRejectReason('');
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('rechargeRequests.rejectFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container">
      <Breadcrumb items={[{ label: t('rechargeRequests.breadcrumbBilling') }, { label: t('rechargeRequests.title') }]} />
      <div className="page-header"><h1 className="page-title">{t('rechargeRequests.title')}</h1></div>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        <button className={`btn ${tab === 'pending' ? 'btn-primary' : 'btn-ghost'} btn-sm`} onClick={() => setTab('pending')}>{t('rechargeRequests.tabPending')}</button>
        <button className={`btn ${tab === 'reviewed' ? 'btn-primary' : 'btn-ghost'} btn-sm`} onClick={() => setTab('reviewed')}>{t('rechargeRequests.tabHistory')}</button>
      </div>

      <div className="card">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-3)' }}>{t('rechargeRequests.loading')}</div>
        ) : items.length === 0 ? (
          <EmptyState icon="&#128221;" description={tab === 'pending' ? t('rechargeRequests.emptyPending') : t('rechargeRequests.emptyHistory')} />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={th}>{t('rechargeRequests.colTime')}</th>
                  <th style={th}>{t('rechargeRequests.colUser')}</th>
                  <th style={{ ...th, textAlign: 'right' }}>{t('rechargeRequests.colReqAmount')}</th>
                  {tab === 'reviewed' && <th style={{ ...th, textAlign: 'right' }}>{t('rechargeRequests.colCredited')}</th>}
                  {tab === 'reviewed' && <th style={th}>{t('rechargeRequests.colStatus')}</th>}
                  {tab === 'reviewed' && <th style={th}>{t('rechargeRequests.colReviewer')}</th>}
                  <th style={th}>{t('rechargeRequests.colNote')}</th>
                  {tab === 'pending' && <th style={th}>{t('rechargeRequests.colActions')}</th>}
                </tr>
              </thead>
              <tbody>
                {items.map(r => (
                  <tr key={r.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ ...td, color: 'var(--text-3)', fontSize: '0.8rem' }}>{formatTime(r.createdAt)}</td>
                    <td style={td}>{r.username || r.userId}</td>
                    <td style={{ ...td, textAlign: 'right' }}>¥{creditsToYuan(r.requestedAmount)}</td>
                    {tab === 'reviewed' && <td style={{ ...td, textAlign: 'right' }}>{r.status === 'approved' ? `¥${creditsToYuan(r.approvedAmount)}` : '-'}</td>}
                    {tab === 'reviewed' && <td style={td}>{t('rechargeRequests.status.' + r.status, { defaultValue: r.status })}</td>}
                    {tab === 'reviewed' && <td style={{ ...td, color: 'var(--text-2)' }}>{r.reviewerName || '-'}</td>}
                    <td style={td}>{r.reviewRemark || r.remark || '-'}</td>
                    {tab === 'pending' && (
                      <td style={td}>
                        <button className="btn btn-primary btn-sm" style={{ marginRight: '0.4rem' }} onClick={() => openApprove(r)}>{t('rechargeRequests.approve')}</button>
                        <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={() => setRejectTarget(r)}>{t('rechargeRequests.reject')}</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 批准弹窗 */}
      <Modal open={!!approveTarget} onClose={() => setApproveTarget(null)} title={t('rechargeRequests.approveTitle')} width="420px"
        footer={<>
          <button className="btn btn-ghost" onClick={() => setApproveTarget(null)} disabled={busy}>{t('rechargeRequests.cancel')}</button>
          <button className="btn btn-primary" onClick={submitApprove} disabled={busy}>{busy ? t('rechargeRequests.processing') : t('rechargeRequests.approveSubmit')}</button>
        </>}>
        {approveTarget && (
          <div>
            <p style={{ color: 'var(--text-2)', marginBottom: '0.75rem' }}>
              {t('rechargeRequests.approveSummary', { user: approveTarget.username, amount: creditsToYuan(approveTarget.requestedAmount) })}
            </p>
            <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-2)', marginBottom: '0.35rem' }}>{t('rechargeRequests.creditLabel')}</label>
            <input type="number" className="input" style={{ width: '100%', marginBottom: '0.75rem' }} value={approveAmount} min="0" step="0.01" onChange={e => setApproveAmount(e.target.value)} />
            <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-2)', marginBottom: '0.35rem' }}>{t('rechargeRequests.remarkOptional')}</label>
            <textarea className="input" style={{ width: '100%', minHeight: '56px' }} value={approveRemark} onChange={e => setApproveRemark(e.target.value)} />
          </div>
        )}
      </Modal>

      {/* 拒绝弹窗 */}
      <Modal open={!!rejectTarget} onClose={() => { setRejectTarget(null); setRejectReason(''); }} title={t('rechargeRequests.rejectTitle')} width="420px"
        footer={<>
          <button className="btn btn-ghost" onClick={() => { setRejectTarget(null); setRejectReason(''); }} disabled={busy}>{t('rechargeRequests.cancel')}</button>
          <button className="btn btn-primary" style={{ background: 'var(--danger)' }} onClick={submitReject} disabled={busy}>{busy ? t('rechargeRequests.processing') : t('rechargeRequests.rejectSubmit')}</button>
        </>}>
        {rejectTarget && (
          <div>
            <p style={{ color: 'var(--text-2)', marginBottom: '0.75rem' }}>
              {t('rechargeRequests.rejectSummary', { user: rejectTarget.username, amount: creditsToYuan(rejectTarget.requestedAmount) })}
            </p>
            <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-2)', marginBottom: '0.35rem' }}>{t('rechargeRequests.reasonLabel')}</label>
            <textarea className="input" style={{ width: '100%', minHeight: '64px' }} value={rejectReason} onChange={e => setRejectReason(e.target.value)} />
          </div>
        )}
      </Modal>
    </div>
  );
}

const th: CSSProperties = { textAlign: 'left', padding: '0.6rem 0.75rem', fontWeight: 600, color: 'var(--text-2)', whiteSpace: 'nowrap' };
const td: CSSProperties = { padding: '0.6rem 0.75rem', whiteSpace: 'nowrap' };
